export type EditorWorkspaceId = 'edit' | 'color' | 'animate'
export type EditorColorComparisonMode = 'off' | 'before' | 'split'

export type EditorSidebarTab = 'media' | 'text' | 'shapes' | 'effects' | 'transitions' | 'ai'
export type EditorClipInspectorTab = 'video' | 'audio' | 'effects'

export interface EditorWorkspaceLayout {
  colorScopesOpen: boolean
  colorComparisonMode: EditorColorComparisonMode
  colorSplitPosition: number
  clipInspectorTab: EditorClipInspectorTab
  activeTab: EditorSidebarTab
  propertiesFullColumn: boolean
}

const EDITOR_WORKSPACE_PRESETS: Record<EditorWorkspaceId, EditorWorkspaceLayout> = {
  edit: {
    colorScopesOpen: false,
    colorComparisonMode: 'off',
    colorSplitPosition: 0.5,
    clipInspectorTab: 'video',
    activeTab: 'media',
    propertiesFullColumn: false,
  },
  color: {
    colorScopesOpen: true,
    colorComparisonMode: 'off',
    colorSplitPosition: 0.5,
    clipInspectorTab: 'effects',
    activeTab: 'effects',
    propertiesFullColumn: true,
  },
  animate: {
    colorScopesOpen: false,
    colorComparisonMode: 'off',
    colorSplitPosition: 0.5,
    clipInspectorTab: 'video',
    activeTab: 'media',
    propertiesFullColumn: false,
  },
}

export const EDITOR_WORKSPACE_TIMELINE_SIZE: Record<EditorWorkspaceId, number | null> = {
  edit: null,
  color: 18,
  animate: null,
}

const DEFAULT_EDITOR_WORKSPACE: EditorWorkspaceId = 'edit'

const SIDEBAR_TABS: readonly EditorSidebarTab[] = [
  'media',
  'text',
  'shapes',
  'effects',
  'transitions',
  'ai',
]

const CLIP_INSPECTOR_TABS: readonly EditorClipInspectorTab[] = ['video', 'audio', 'effects']

function isSidebarTab(value: unknown): value is EditorSidebarTab {
  return SIDEBAR_TABS.includes(value as EditorSidebarTab)
}

function isClipInspectorTab(value: unknown): value is EditorClipInspectorTab {
  return CLIP_INSPECTOR_TABS.includes(value as EditorClipInspectorTab)
}

function isColorComparisonMode(value: unknown): value is EditorColorComparisonMode {
  return value === 'off' || value === 'before' || value === 'split'
}

function normalizeColorSplitPosition(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.min(0.95, Math.max(0.05, value))
}

export function normalizeEditorWorkspaceId(value: unknown): EditorWorkspaceId {
  if (value === 'color') return 'color'
  if (value === 'animate') return 'animate'
  return DEFAULT_EDITOR_WORKSPACE
}

export function normalizeEditorWorkspaceLayout(
  value: unknown,
  workspace: EditorWorkspaceId,
): EditorWorkspaceLayout {
  const preset = EDITOR_WORKSPACE_PRESETS[workspace]
  if (!value || typeof value !== 'object') return preset

  const candidate = value as Partial<Record<keyof EditorWorkspaceLayout, unknown>>

  return {
    colorScopesOpen:
      typeof candidate.colorScopesOpen === 'boolean'
        ? candidate.colorScopesOpen
        : preset.colorScopesOpen,
    colorComparisonMode: isColorComparisonMode(candidate.colorComparisonMode)
      ? candidate.colorComparisonMode
      : preset.colorComparisonMode,
    colorSplitPosition: normalizeColorSplitPosition(
      candidate.colorSplitPosition,
      preset.colorSplitPosition,
    ),
    clipInspectorTab: isClipInspectorTab(candidate.clipInspectorTab)
      ? candidate.clipInspectorTab
      : preset.clipInspectorTab,
    activeTab: isSidebarTab(candidate.activeTab) ? candidate.activeTab : preset.activeTab,
    propertiesFullColumn:
      typeof candidate.propertiesFullColumn === 'boolean'
        ? candidate.propertiesFullColumn
        : preset.propertiesFullColumn,
  }
}
